import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import { Button, LoadingPanel } from '@gitbook/spine-react';

import { Stepper, type StepperProps } from '../Stepper';
import type { ModalSize } from './Modal';

export interface ModalStepperLayoutProps extends StepperProps {
    /** Modal content */
    children?: React.ReactNode;

    /** Callback when clicking the button to step back */
    onStepBack?: () => void;

    /** Size of the modal, default to medium */
    size?: ModalSize;

    /** Whether to animate the active step */
    stepperAnimate?: boolean;

    /** List of errored steps */
    erroredSteps?: number[];
}

/**
 * Inner layout for a modal with a stepper top and navigation buttons on the side.
 *
 * <Modal>
 *   <ModalStepperLayout {...props}>
 *      <Content />
 *   </ModalSidebarLayout>
 * </Modal>
 */
export function ModalStepperLayout(props: ModalStepperLayoutProps): React.ReactElement {
    const {
        children,
        onStepBack,
        steps,
        completedSteps,
        currentStepIndex,
        stepperAnimate = false,
        erroredSteps,
    } = props;

    return (
        <div className="p-10">
            <div className="flex w-4 items-start md:w-20">
                {currentStepIndex > 0 && onStepBack ? (
                    <Button
                        kind="secondary"
                        label="Back"
                        leadingIcon={Icon.ArrowLeft}
                        onPress={onStepBack}
                        iconOnly
                    />
                ) : null}
            </div>
            <div className="flex flex-1 flex-col">
                <Stepper
                    steps={steps}
                    completedSteps={completedSteps}
                    currentStepIndex={currentStepIndex}
                    styles="px-2"
                    // This component is a pre-made layout for horizontal steppers and thus doesn't allow a prop for changing direction. If a vertical stepper is needed, use the Stepper component directly.
                    orientation={'horizontal'}
                    animate={stepperAnimate}
                    erroredSteps={erroredSteps}
                />
                <React.Suspense fallback={<LoadingPanel size="large" />}>
                    {children ?? steps[currentStepIndex]?.content}
                </React.Suspense>
            </div>
        </div>
    );
}
